import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";

import type { UiTheme } from "../theme";
import { SectionHeader } from "./SectionHeader";

// <PickerSessionBand> — DS-42. The place chip, with its SCOPE said out loud.
//
// The defect it fixes: the swap picker does three different jobs wearing one
// idiom ("text + ▾", three times on three consecutive rows), and the one with
// the WIDEST scope — the gym, which writes where the whole SESSION happens —
// sat in the title row, i.e. the spot that reads as the least consequential
// thing on screen. Naming the band is what separates it from the two
// entry-scoped controls below it; without the name the caret is just a third
// identical chip, told apart only by a border that DS-37 measures at 1.1:1.
//
// ⛔ Mounted by BOTH pickers, and that is not incidental. Antenore's rule
// (EQ-1 D2b): *"nel add e nel replace, quello deve imperativamente utilizzare
// la stessa primitiva"* — same primitive AND same slot, so the two headers
// cannot drift into answering "which gym" their own way. Moving the chip out
// of the header in the swap picker alone would have created exactly that
// drift, so the band moves in both.
//
// No fill: a `surface2` band would read as one of the list's own boxed group
// headers (ExerciseSwapPicker.styles `sectionHeader`), which is the confusion
// this voice exists to remove. A hairline underneath is enough to strip it off
// the header.
export type PickerSessionBandProps = {
  theme: UiTheme;
  /** The scope word itself, localized ("Session"). */
  label: string;
  /** The two nodes `useGymChip` hands back. Omitted when the host has no
   *  session to place — then the band renders nothing at all rather than an
   *  empty labelled strip. */
  gymChip?: { trigger: ReactNode; tray: ReactNode };
};

export function PickerSessionBand({
  theme,
  label,
  gymChip,
}: PickerSessionBandProps) {
  if (!gymChip) return null;
  const styles = makeStyles(theme);
  return (
    <View>
      <View style={styles.row}>
        <SectionHeader theme={theme} title={label} style={styles.label} />
        {gymChip.trigger}
      </View>
      {gymChip.tray}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    row: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      paddingHorizontal: s.s5,
      paddingBottom: s.s3,
      borderBottomWidth: StyleSheet.hairlineWidth,
      // ⛔ `borderStrong`, NOT `border`. Measured across all six family×mode
      // combinations, `border` here runs 1.22:1 to 2.46:1 — under the 3:1
      // non-text floor in every one of them, i.e. the divider is not there.
      // Shipping it would have re-created, inside the voice that exists to
      // remove it, the very defect DS-37 names: a band told apart from its
      // neighbours by an edge nobody can see is an unnamed band. `borderStrong`
      // measures 3.46:1 to 4.55:1 across the same six, the same move
      // `machineBadge` and `Chip`'s `prominent` already made under DS-9/DS-23.
      borderBottomColor: p.borderStrong,
    },
    // The label takes the slack so the chip settles on the right edge, and a
    // long scope word wraps instead of shoving the control off-screen.
    label: { flexShrink: 1, marginRight: "auto" },
  });
}
